import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CRow,
  CCol,
  CForm,
  CFormInput,
  CFormTextarea,
  CFormLabel,
  CButton,
  CSpinner,
  CAlert,
  CFormSelect,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilSave, cilCheckCircle } from "@coreui/icons";
import { createSkill, apiService, apiClient } from "../services/api";
import { useRef, useEffect } from "react";

const SkillCreator = ({ onSkillCreated }) => {
  const navigate = useNavigate();

  const [newSkill, setNewSkill] = useState({
    skillName: "",
    invocationName: "", // Custom invocation name field
    soundUrl: "",
    premiumSoundUrl: "",
    smallIconUrl: "",
    largeIconUrl: "",
    description: "",
    shortDescription: "",
    category: "SLEEP_AND_AMBIENT_SOUNDS",
    examplePhrases: [
      "Alexa, open {skill name}",
      "Alexa, play {skill name}",
      "Alexa, launch {skill name}",
    ].join("\n"),
    keywords: "",
    enablePremium: false,
    premiumSubscription: {
      name: "",
      description: "",
      currency: "USD",
      amount: 0.99,
      freeTrialDays: 21,
    },
  });

  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createError, setCreateError] = useState(null);
  const [uploadingSmallIcon, setUploadingSmallIcon] = useState(false);
  const [uploadingLargeIcon, setUploadingLargeIcon] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [step, setStep] = useState(1); // Track form step (1: basic, 2: advanced, 3: premium)
  const [invocationNameError, setInvocationNameError] = useState("");
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const duplicateCheckTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (duplicateCheckTimeoutRef.current) {
        clearTimeout(duplicateCheckTimeoutRef.current);
      }
    };
  }, []);

  // Validation functions for each step
  const isStep1Valid = () => {
    return (
      newSkill.skillName.trim() !== "" &&
      newSkill.description.trim() !== "" &&
      newSkill.shortDescription.trim() !== "" &&
      newSkill.soundUrl.trim() !== ""
    );
  };

  const isStep2Valid = () => {
    return (
      newSkill.smallIconUrl &&
      newSkill.largeIconUrl &&
      newSkill.examplePhrases.trim() !== ""
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Special handling for invocation name - validate ALL rules immediately while typing
    if (name === "invocationName") {
      // Convert to lowercase as user types, preserving ALL characters including spaces
      const lowerValue = value.toLowerCase();

      // Use a local variable to track errors (since state updates are async)
      let errorMessage = "";

      // Validate ALL rules immediately while typing
      const trimmedValue = lowerValue.trim();
      const words = trimmedValue.split(/\s+/).filter(Boolean); // Define words early for use in duplicate check
      if (trimmedValue.length > 0) {
        // 1. Check for invalid characters - allow letters, spaces, and possessive apostrophes
        if (/[^a-z\s']/.test(lowerValue)) {
          errorMessage = "Invocation name must contain only letters, spaces, and possessive apostrophes (e.g., \"sam's science trivia\")";
        }
        // Check apostrophe usage
        else if (lowerValue.includes("'")) {
          if (!/'[a-z]/.test(trimmedValue)) {
            errorMessage = "Apostrophes must be used in possessive form (e.g., \"sam's\")";
          } else if (trimmedValue.startsWith("'") || trimmedValue.endsWith("'")) {
            errorMessage = "Apostrophes cannot be at the start or end of the invocation name";
          }
        }
        // 2. Check if it starts with a letter (only after trimming)
        else if (!/^[a-z]/.test(trimmedValue)) {
          errorMessage = "Invocation name must start with a letter";
        }
        // 3. Check for prohibited words while typing
        else {
          // Check for wake words
          const wakeWords = ['alexa', 'amazon', 'echo', 'computer'];
          for (const wakeWord of wakeWords) {
            if (new RegExp(`\\b${wakeWord}\\b`, 'i').test(trimmedValue)) {
              errorMessage = `Cannot contain the wake word "${wakeWord}"`;
              break;
            }
          }

          // Check for launch phrases (only if no error found yet)
          if (!errorMessage) {
            const launchPhrases = ['run', 'start', 'play', 'open', 'launch', 'ask', 'enable'];
            for (const phrase of launchPhrases) {
              if (new RegExp(`\\b${phrase}\\b`, 'i').test(trimmedValue)) {
                errorMessage = `Cannot contain the launch phrase "${phrase}"`;
                break;
              }
            }
          }

          // Check for "skill" or "app" (only if no error found yet)
          if (!errorMessage && /\b(skill|app)\b/i.test(trimmedValue)) {
            errorMessage = 'Cannot contain the words "skill" or "app"';
          }

          // 4. Check word count and grammar rules - ALWAYS check these immediately
          if (!errorMessage) {
            const wordCount = words.length;

            // Check word count - must have 2+ words
            // Show error immediately when user has typed a single word (not while typing)
            if (wordCount === 1 && !lowerValue.endsWith(' ')) {
              // User has finished typing first word, show error immediately
              errorMessage = "Invocation name must have two or more words";
            }
            // Check 2-word grammar constraint - show immediately when 2 words are detected
            else if (wordCount === 2) {
              const articles = ['the', 'a', 'an'];
              const prepositions = ['for', 'to', 'of', 'about', 'with', 'from', 'in', 'on', 'at', 'by', 'into', 'onto', 'upon', 'over', 'under', 'through', 'during', 'before', 'after', 'above', 'below', 'between', 'among', 'beside', 'besides', 'beyond', 'within', 'without', 'against', 'across', 'around', 'near', 'toward', 'towards', 'until', 'till', 'since', 'throughout', 'via', 'per', 'except', 'excluding', 'including', 'concerning', 'regarding', 'respecting', 'considering', 'following', 'pending'];

              const firstWord = words[0].replace(/'s$/, ''); // Remove possessive
              const secondWord = words[1].replace(/'s$/, ''); // Remove possessive

              if (articles.includes(firstWord) || articles.includes(secondWord)) {
                errorMessage = 'Two-word names cannot contain articles ("the", "a", "an")';
              } else if (prepositions.includes(firstWord) || prepositions.includes(secondWord)) {
                errorMessage = 'Two-word names cannot contain prepositions ("for", "to", "of", "about", etc.)';
              }
            }
            // Check for more than 3 words (warning)
            else if (wordCount > 3) {
              errorMessage = "Invocation name should have no more than 3 words ideally";
            }
          }
        }
      }

      // Update error state
      setInvocationNameError(errorMessage);

      // If no validation errors and we have at least 2 words, check for duplicates
      if (!errorMessage && trimmedValue.length > 0 && words.length >= 2) {
        // Clear any existing timeout
        if (duplicateCheckTimeoutRef.current) {
          clearTimeout(duplicateCheckTimeoutRef.current);
        }
        
        // Debounce duplicate check (wait 800ms after user stops typing)
        duplicateCheckTimeoutRef.current = setTimeout(async () => {
          try {
            setIsCheckingDuplicate(true);
            const duplicateResult = await apiService.checkDuplicateInvocationName(trimmedValue);
            
            if (duplicateResult.isDuplicate) {
              setInvocationNameError(
                `This invocation name is already in use by skill "${duplicateResult.existingSkill.name}"`
              );
            } else if (!errorMessage) {
              // Only clear error if there are no other validation errors
              setInvocationNameError("");
            }
          } catch (duplicateError) {
            console.warn('Error checking duplicate invocation name:', duplicateError);
            // Don't show error to user if duplicate check fails - it's not critical
          } finally {
            setIsCheckingDuplicate(false);
          }
        }, 800); // Wait 800ms after user stops typing
      } else if (errorMessage) {
        // Clear duplicate check timeout if there are validation errors
        if (duplicateCheckTimeoutRef.current) {
          clearTimeout(duplicateCheckTimeoutRef.current);
        }
        setIsCheckingDuplicate(false);
      }

      // Store the lowercase value (preserving ALL spaces including leading/trailing)
      // This allows users to type spaces freely
      setNewSkill((prev) => ({ ...prev, [name]: lowerValue }));
    } else {
      setNewSkill((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Upload small icon to S3
  const handleSmallIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only accept PNG
    if (!file.type.match("image/png")) {
      setUploadError("Small icon must be a PNG image");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError("Small icon must be less than 2MB");
      return;
    }

    setUploadingSmallIcon(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("image", file);

      // Use apiClient from api.js which includes Firebase authentication
      const response = await apiClient.post("/api/upload/image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setNewSkill((prev) => ({
        ...prev,
        smallIconUrl: response.data.imageUrl,
      }));
    } catch (error) {
      console.error("Error uploading small icon:", error);
      const errorMessage = error.response?.data?.error || error.message || "Upload failed";
      setUploadError(`Failed to upload small icon: ${errorMessage}`);
    } finally {
      setUploadingSmallIcon(false);
    }
  };

  // Upload large icon to S3
  const handleLargeIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only accept PNG
    if (!file.type.match("image/png")) {
      setUploadError("Large icon must be a PNG image");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("Large icon must be less than 5MB");
      return;
    }

    setUploadingLargeIcon(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("image", file);

      // Use apiClient from api.js which includes Firebase authentication
      const response = await apiClient.post("/api/upload/image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setNewSkill((prev) => ({
        ...prev,
        largeIconUrl: response.data.imageUrl,
      }));
    } catch (error) {
      console.error("Error uploading large icon:", error);
      const errorMessage = error.response?.data?.error || error.message || "Upload failed";
      setUploadError(`Failed to upload large icon: ${errorMessage}`);
    } finally {
      setUploadingLargeIcon(false);
    }
  };

  const handleCreateSkill = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    setCreateResult(null);

    // Validate invocation name if provided
    // All validation is done in real-time, but double-check on submit
    if (newSkill.invocationName && newSkill.invocationName.trim()) {
      if (invocationNameError) {
        setCreateError(`Invocation name error: ${invocationNameError}`);
        window.scrollTo(0, 0);
        setCreating(false);
        return;
      }
      
      // Final validation check - ensure word count is correct
      const trimmedInvocation = newSkill.invocationName.trim();
      const words = trimmedInvocation.split(/\s+/).filter(Boolean);
      
      if (words.length < 2) {
        setInvocationNameError("Invocation name must have two or more words");
        setCreateError("Please fix the invocation name errors before submitting");
        window.scrollTo(0, 0);
        setCreating(false);
        return;
      }
    }

    // Process form data
    const skillData = {
      ...newSkill,
      invocationName: newSkill.invocationName.trim() || undefined, // Send undefined if empty so backend can auto-generate
      examplePhrases: newSkill.examplePhrases
        .split("\n")
        .filter((phrase) => phrase.trim().length > 0),
      keywords: newSkill.keywords
        .split(",")
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0),
    };

    try {
      // Create the skill first
      const result = await createSkill(skillData);
      setCreateResult(result);

      // Store invocation name in localStorage for later use when building interaction model
      // Prefer the invocation name returned from backend (if available), otherwise use the one from skillData
      if (result.skillId) {
        // Use invocation name from result if available, otherwise fall back to skillData
        const invocationToSave = (result.invocationName && result.invocationName.trim()) 
          ? result.invocationName.trim()
          : (skillData.invocationName && skillData.invocationName.trim())
          ? skillData.invocationName.trim()
          : null;
          
        if (invocationToSave) {
          localStorage.setItem(`skill_${result.skillId}_invocationName`, invocationToSave);
        } else {
          // Remove any existing entry if invocation name was cleared
          localStorage.removeItem(`skill_${result.skillId}_invocationName`);
        }
      }

      // If premium subscription is enabled, create the subscription product
      if (newSkill.enablePremium && result.skillId) {
        try {
          const { premiumSubscription } = newSkill;
          await apiService.createSubscription(result.skillId, {
            subscriptionName: premiumSubscription.name,
            description:
              premiumSubscription.description ||
              `Unlock premium features with ${newSkill.skillName} subscription`,
            currency: premiumSubscription.currency,
            amount: premiumSubscription.amount,
            smallIconUrl: newSkill.smallIconUrl,
            largeIconUrl: newSkill.largeIconUrl,
            freeTrialDays: premiumSubscription.freeTrialDays,
            // Custom product prompts required by Amazon
            purchasePrompt: premiumSubscription.purchasePrompt,
            purchaseConfirmation: premiumSubscription.purchaseConfirmation,
            boughtCardDescription: premiumSubscription.boughtCardDescription,
            cancelPrompt: premiumSubscription.cancelPrompt,
            cancelledCardDescription:
              premiumSubscription.cancelledCardDescription,
            renewedCardDescription: premiumSubscription.renewedCardDescription,
          });
        } catch (subscriptionError) {
          console.error("Error creating subscription:", subscriptionError);
          // We don't fail the whole process if subscription creation fails
        }
      }

      // Reset form after successful creation
      setNewSkill({
        skillName: "",
        invocationName: "",
        soundUrl: "",
        premiumSoundUrl: "",
        smallIconUrl: "",
        largeIconUrl: "",
        description: "",
        shortDescription: "",
        category: "SLEEP_AND_AMBIENT_SOUNDS",
        examplePhrases: [
          "Alexa, open {skill name}",
          "Alexa, play {skill name}",
          "Alexa, launch {skill name}",
        ].join("\n"),
        keywords: "",
        enablePremium: false,
        premiumSubscription: {
          name: "",
          description: "",
          currency: "USD",
          amount: 0.99,
          freeTrialDays: 21,
          // Required custom prompt fields
          purchasePrompt:
            "Would you like to subscribe to premium content and unlock additional features?",
          purchaseConfirmation:
            "Thank you for subscribing! You now have access to all premium content.",
          boughtCardDescription:
            "You now have a subscription. Enjoy premium content and features!",
          cancelPrompt: "Are you sure you want to cancel your subscription?",
          cancelledCardDescription: "Your subscription has been cancelled.",
          renewedCardDescription: "Your subscription has been renewed.",
        },
      });
      setInvocationNameError("");

      // Notify parent component to refresh skills list
      if (onSkillCreated) onSkillCreated();

      // Auto-navigate to the skills list after 2 seconds on success
      setTimeout(() => {
        navigate("/skills");
      }, 2000);
    } catch (err) {
      setCreateError(err.response?.data?.error || "Failed to create skill");
      window.scrollTo(0, 0); // Scroll to top to see the error
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Create New Alexa Skill</h2>
      </div>

      {createError && (
        <CAlert color="danger" className="mb-4">
          {createError}
        </CAlert>
      )}

      {uploadError && (
        <CAlert color="danger" className="mb-4">
          {uploadError}
        </CAlert>
      )}

      {createResult && (
        <CAlert color="success" className="mb-4">
          <CIcon icon={cilCheckCircle} className="me-2" />
          Skill created successfully! Redirecting to skills list...
        </CAlert>
      )}

      <CCard className="mb-4">
        <CCardHeader className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Skill Information</strong>
              <div className="small text-muted">
                All fields marked with * are required
              </div>
            </div>
            <div>
              <CButton
                color={step === 1 ? "primary" : "light"}
                size="sm"
                className="me-2"
                onClick={() => setStep(1)}
                disabled={step === 1}
              >
                Basic Info
              </CButton>
              <CButton
                color={step === 2 ? "primary" : "light"}
                size="sm"
                onClick={() => setStep(2)}
                disabled={step === 2}
              >
                Advanced Settings
              </CButton>
            </div>
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleCreateSkill}>
            {step === 1 && (
              // Step 1: Basic Information
              <>
                <CRow className="mb-3">
                  <CCol md={6}>
                    <CFormLabel htmlFor="skillName">Skill Name *</CFormLabel>
                    <CFormInput
                      id="skillName"
                      name="skillName"
                      value={newSkill.skillName}
                      onChange={handleInputChange}
                      placeholder="Enter a name for your skill"
                      required
                    />
                    <div className="form-text text-muted">
                      This will be the name users see in the Alexa Skills Store.
                      Start with a letter, Only letters and spaces, Easy to
                      pronounce, 2-3 words ideally.
                    </div>
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel htmlFor="category">Category *</CFormLabel>
                    <CFormSelect
                      id="category"
                      name="category"
                      value={newSkill.category}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="MUSIC_AND_AUDIO_ACCESSORIES">
                        Accessories
                      </option>
                      <option value="SLEEP_AND_AMBIENT_SOUNDS">
                        Sleep & Ambient Sounds
                      </option>
                      <option value="CHILDRENS_MEDITATION_AND_MINDFULNESS">
                        Yoga, Meditation & Mindfulness
                      </option>
                      <option value="CHILDRENS_MUSIC_AND_AUDIO">
                        Music & Audio
                      </option>
                    </CFormSelect>
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CCol md={12}>
                    <CFormLabel htmlFor="invocationName">
                      Invocation Name (Optional)
                    </CFormLabel>
                    <CFormInput
                      id="invocationName"
                      name="invocationName"
                      value={newSkill.invocationName}
                      onChange={handleInputChange}
                      placeholder="e.g., my sleep sounds"
                      className={invocationNameError ? "is-invalid" : ""}
                    />
                    {invocationNameError ? (
                      <div className="invalid-feedback d-block">
                        {invocationNameError}
                      </div>
                    ) : isCheckingDuplicate ? (
                      <div className="form-text text-info">
                        <small>🔍 Checking if invocation name is available...</small>
                      </div>
                    ) : (
                      <div className="form-text text-muted">
                        The phrase users say to invoke your skill (e.g., "Alexa, open my sleep sounds").
                        Leave empty to auto-generate from skill name. Must be lowercase, 2-3 words, letters and spaces only.
                      </div>
                    )}
                  </CCol>
                </CRow>
                <CRow className="mb-3">
                  <CCol md={12}>
                    <CFormLabel htmlFor="shortDescription">
                      Short Description *
                    </CFormLabel>
                    <CFormInput
                      id="shortDescription"
                      name="shortDescription"
                      value={newSkill.shortDescription}
                      onChange={handleInputChange}
                      placeholder="Enter a short description (max 120 characters)"
                      maxLength={120}
                      required
                    />
                    <div className="form-text text-muted">
                      This short description will appear in the Alexa Skills
                      Store. Maximum 120 characters.
                      <span className="float-end">
                        {newSkill.shortDescription.length}/120
                      </span>
                    </div>
                  </CCol>
                </CRow>

                <CRow className="mb-3">
                  <CCol md={12}>
                    <CFormLabel htmlFor="description">Description *</CFormLabel>
                    <CFormTextarea
                      id="description"
                      name="description"
                      value={newSkill.description}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Describe your skill (max 4000 characters)"
                      required
                    />
                  </CCol>
                </CRow>

                <CRow className="mb-3">
                  <CCol md={6}>
                    <CFormLabel htmlFor="soundUrl">Sound URL *</CFormLabel>
                    <CFormInput
                      id="soundUrl"
                      name="soundUrl"
                      value={newSkill.soundUrl}
                      onChange={handleInputChange}
                      placeholder="https://feeds.soundcloud.com/stream/939594856-therealsleepsounds-ocean-sounds-5min.mp3"
                      required
                    />
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel htmlFor="premiumSoundUrl">
                      Premium Sound URL
                    </CFormLabel>
                    <CFormInput
                      id="premiumSoundUrl"
                      name="premiumSoundUrl"
                      value={newSkill.premiumSoundUrl}
                      onChange={handleInputChange}
                      placeholder="https://feeds.soundcloud.com/stream/939564109-therealsleepsounds-ocean-5hr-long.mp3"
                    />
                  </CCol>
                </CRow>

                <div className="d-flex justify-content-between mt-4">
                  <div></div>
                  <CButton
                    type="button"
                    color="primary"
                    onClick={() => setStep(2)}
                    disabled={!isStep1Valid()}
                    title={
                      !isStep1Valid()
                        ? "Please fill in all required fields marked with *"
                        : ""
                    }
                  >
                    Next: Advanced Settings
                  </CButton>
                </div>
              </>
            )}

            {step === 2 && (
              // Step 2: Advanced Settings
              <>
                <CRow className="mb-3">
                  <CCol md={6}>
                    <CFormLabel htmlFor="smallIconFile">
                      Small Icon (108x108px) *
                    </CFormLabel>
                    <div className="d-flex">
                      <div className="position-relative flex-grow-1">
                        <CFormInput
                          type="file"
                          id="smallIconFile"
                          accept="image/png"
                          onChange={handleSmallIconUpload}
                          className="me-2"
                          disabled={uploadingSmallIcon}
                        />
                        {uploadingSmallIcon && (
                          <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                            <CSpinner size="sm" />
                          </div>
                        )}
                      </div>
                      {newSkill.smallIconUrl && (
                        <div className="ms-2 d-flex align-items-center">
                          <span className="small text-success">✓ Uploaded</span>
                        </div>
                      )}
                    </div>
                    <div className="form-text text-muted">
                      Upload a 108x108px PNG image only
                    </div>
                  </CCol>
                  <CCol md={6}>
                    <CFormLabel htmlFor="largeIconFile">
                      Large Icon (512x512px) *
                    </CFormLabel>
                    <div className="d-flex">
                      <div className="position-relative flex-grow-1">
                        <CFormInput
                          type="file"
                          id="largeIconFile"
                          accept="image/png"
                          onChange={handleLargeIconUpload}
                          className="me-2"
                          disabled={uploadingLargeIcon}
                        />
                        {uploadingLargeIcon && (
                          <div className="position-absolute top-50 end-0 translate-middle-y me-4">
                            <CSpinner size="sm" />
                          </div>
                        )}
                      </div>
                      {newSkill.largeIconUrl && (
                        <div className="ms-2 d-flex align-items-center">
                          <span className="small text-success">✓ Uploaded</span>
                        </div>
                      )}
                    </div>
                    <div className="form-text text-muted">
                      Upload a 512x512px PNG image only
                    </div>
                  </CCol>
                </CRow>

                <CRow className="mb-3">
                  <CCol md={12}>
                    <CFormLabel htmlFor="examplePhrases">
                      Example Phrases *
                    </CFormLabel>
                    <CFormTextarea
                      id="examplePhrases"
                      name="examplePhrases"
                      value={newSkill.examplePhrases}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Enter each phrase on a new line"
                      required
                    />
                    <div className="form-text text-muted">
                      Do not edit these phrases. These will be set automatically
                      with your skill name.{" "}
                    </div>
                  </CCol>
                </CRow>

                <CRow className="mb-3">
                  <CCol md={12}>
                    <CFormLabel htmlFor="keywords">Keywords</CFormLabel>
                    <CFormInput
                      id="keywords"
                      name="keywords"
                      value={newSkill.keywords}
                      onChange={handleInputChange}
                      placeholder="rain, sleep, relax, meditation"
                    />
                    <div className="form-text text-muted">
                      Comma-separated keywords to help users find your skill. You can add up to 30 keywords, but it’s recommended to use around 15 high-quality keywords rather than filling all 30 with weaker ones.
                    </div>
                  </CCol>
                </CRow>

                <div className="d-flex justify-content-between mt-4">
                  <CButton
                    type="button"
                    color="light"
                    onClick={() => setStep(1)}
                  >
                    Back to Basic Info
                  </CButton>
                  <CButton
                    type="button"
                    color="primary"
                    onClick={() => setStep(3)}
                    disabled={!isStep2Valid()}
                    title={
                      !isStep2Valid()
                        ? "Please upload both icons and provide example phrases"
                        : ""
                    }
                  >
                    Next: Premium Options
                  </CButton>
                </div>
              </>
            )}

            {step === 3 && (
              // Step 3: Premium Subscription Options
              <>
                <CRow className="mb-4">
                  <CCol md={12}>
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="enablePremium"
                        checked={newSkill.enablePremium}
                        onChange={(e) => {
                          setNewSkill((prev) => ({
                            ...prev,
                            enablePremium: e.target.checked,
                            premiumSubscription: {
                              ...prev.premiumSubscription,
                              name:
                                e.target.checked &&
                                !prev.premiumSubscription.name
                                  ? `${prev.skillName} Pro`
                                  : prev.premiumSubscription.name,
                            },
                          }));
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="enablePremium"
                      >
                        Enable Premium Subscription ($0.99/month with 21-day
                        free trial)
                      </label>
                    </div>
                  </CCol>
                </CRow>

                {newSkill.enablePremium && (
                  <>
                    <CRow className="mb-3">
                      <CCol md={6}>
                        <CFormLabel htmlFor="subscriptionName">
                          Subscription Name *
                        </CFormLabel>
                        <CFormInput
                          id="subscriptionName"
                          value={newSkill.premiumSubscription.name}
                          onChange={(e) => {
                            setNewSkill((prev) => ({
                              ...prev,
                              premiumSubscription: {
                                ...prev.premiumSubscription,
                                name: e.target.value,
                              },
                            }));
                          }}
                          placeholder="e.g. My Skill Pro"
                          required={newSkill.enablePremium}
                        />
                        <div className="form-text text-muted">
                          The name of your premium subscription as it will
                          appear to customers
                        </div>
                      </CCol>
                      <CCol md={6}>
                        <CFormLabel htmlFor="freeTrialDays">
                          Free Trial Period (days)
                        </CFormLabel>
                        <CFormInput
                          id="freeTrialDays"
                          type="number"
                          min="0"
                          max="365"
                          value={newSkill.premiumSubscription.freeTrialDays}
                          onChange={(e) => {
                            setNewSkill((prev) => ({
                              ...prev,
                              premiumSubscription: {
                                ...prev.premiumSubscription,
                                freeTrialDays: parseInt(e.target.value) || 0,
                              },
                            }));
                          }}
                        />
                        <div className="form-text text-muted">
                          Number of days for the free trial period (default: 21)
                        </div>
                      </CCol>
                    </CRow>

                    <CRow className="mb-3">
                      <CCol md={6}>
                        <CFormLabel htmlFor="subscriptionCurrency">
                          Currency
                        </CFormLabel>
                        <CFormSelect
                          id="subscriptionCurrency"
                          value={newSkill.premiumSubscription.currency}
                          onChange={(e) => {
                            setNewSkill((prev) => ({
                              ...prev,
                              premiumSubscription: {
                                ...prev.premiumSubscription,
                                currency: e.target.value,
                              },
                            }));
                          }}
                        >
                          <option value="USD">USD ($)</option>
                          <option value="GBP">GBP (£)</option>
                        </CFormSelect>
                      </CCol>
                      <CCol md={6}>
                        <CFormLabel htmlFor="subscriptionAmount">
                          Monthly Price
                        </CFormLabel>
                        <CFormInput
                          id="subscriptionAmount"
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={newSkill.premiumSubscription.amount}
                          onChange={(e) => {
                            setNewSkill((prev) => ({
                              ...prev,
                              premiumSubscription: {
                                ...prev.premiumSubscription,
                                amount: parseFloat(e.target.value) || 0.99,
                              },
                            }));
                          }}
                        />
                        <div className="form-text text-muted">
                          Monthly subscription price (default: 0.99)
                        </div>
                      </CCol>
                    </CRow>

                    <CRow className="mb-3">
                      <CCol md={12}>
                        <CFormLabel htmlFor="subscriptionDescription">
                          Subscription Description
                        </CFormLabel>
                        <CFormTextarea
                          id="subscriptionDescription"
                          rows={3}
                          value={newSkill.premiumSubscription.description}
                          onChange={(e) => {
                            setNewSkill((prev) => ({
                              ...prev,
                              premiumSubscription: {
                                ...prev.premiumSubscription,
                                description: e.target.value,
                              },
                            }));
                          }}
                          placeholder={`Unlock premium features with ${newSkill.skillName} subscription`}
                        />
                        <div className="form-text text-muted">
                          Description of what users get with the premium
                          subscription
                        </div>
                      </CCol>
                    </CRow>
                  </>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <CButton
                    type="button"
                    color="light"
                    onClick={() => setStep(2)}
                  >
                    Back to Advanced Settings
                  </CButton>
                  <CButton
                    type="submit"
                    color="primary"
                    disabled={creating}
                    className="d-flex align-items-center"
                  >
                    {creating ? (
                      <>
                        <CSpinner size="sm" className="me-2" />
                        Creating Skill...
                      </>
                    ) : (
                      <>
                        <CIcon icon={cilSave} className="me-2" />
                        Create Skill
                      </>
                    )}
                  </CButton>
                </div>
              </>
            )}
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default SkillCreator;
